/** Plain sentences for everything that can go wrong in a Word turn. Never internal names ("Scholar"). */

import { SnapshotTooLargeError } from "../hands/types";
import { AuthLostError, BackendError, UploadError, WrongProductError } from "../backends/types";

export type TurnStep = "read" | "upload" | "start" | "wait";

function seconds(ms: number | null): string {
  if (ms === null || ms <= 0) return "";
  const s = Math.ceil(ms / 1000);
  return s < 90 ? ` in about ${s} seconds` : ` in about ${Math.ceil(s / 60)} minutes`;
}

export function turnErrorMessage(error: unknown, step: TurnStep): string {
  if (error instanceof AuthLostError) return error.message;
  if (error instanceof SnapshotTooLargeError || error instanceof UploadError || error instanceof WrongProductError) return error.message;
  if (error instanceof BackendError) {
    const s = error.status;
    const detail = error.message;
    if (s === 403) return `Jurisio refused this request from Word: ${detail}`;
    if (s === 404) {
      if (step === "wait") return "Ella lost track of this request (it may have expired). Send it again.";
      if (/snapshot/i.test(detail)) return "Ella couldn't find the document upload. Send your message again.";
      if (/thread|conversation/i.test(detail)) return "That conversation is no longer available. Send your message again to start a new one.";
      return "Ella in Word isn't switched on for this account yet.";
    }
    if (s === 409) return "This conversation changed somewhere else a moment ago. Send your message again.";
    if (s === 413) return "This request is too large for Ella. Try a shorter message or a smaller document.";
    if (s === 429) {
      const base = /already working/i.test(detail) ? detail.replace(/\bAsk\b/g, "Ella") : "Ella is busy with other requests for your account.";
      return `${base} Try again${seconds(error.retryAfterMs) || " in a moment"}.`;
    }
    if (s === 501) return detail;
    if (s === 504) return detail;
    if (s >= 500) return `Jurisio is having trouble right now (${s}). Try again in a moment.`;
    return detail;
  }
  if (error instanceof TypeError) return "Couldn't reach Jurisio. Check your connection and try again.";
  if (step === "read") return `Ella couldn't read this document: ${error instanceof Error ? error.message : String(error)}`;
  return error instanceof Error ? error.message : "Something went wrong.";
}
